import React from 'react';
import { 
  Clock, ShieldCheck, ShieldAlert, UserPlus, 
  UserMinus, Banknote, Info, Activity 
} from 'lucide-react';

interface Log {
  id: string;
  adminName: string;
  action: string;
  targetId: string;
  targetName: string;
  details?: string;
  createdAt: string;
}

const RecentActivity = ({ logs }: { logs: Log[] }) => {
  
  /**
   * 🎨 CONFIGURATION HELPER
   * Assigns specific icons and colors based on the Admin action.
   */
  const getActionConfig = (action: string) => {
    const act = action.toUpperCase();
    
    if (act.includes('VERIFY')) 
      return { icon: <ShieldCheck size={14} />, color: 'text-blue-600', bg: 'bg-blue-50' };
    
    if (act.includes('KICK') || act.includes('DEACTIVATE') || act.includes('REJECT')) 
      return { icon: <UserMinus size={14} />, color: 'text-red-600', bg: 'bg-red-50' };
    
    if (act.includes('APPROVE_PAYMENT') || act.includes('TRANSACTION')) 
      return { icon: <Banknote size={14} />, color: 'text-green-600', bg: 'bg-green-50' };
    
    if (act.includes('ACTIVATE') || act.includes('USER')) 
      return { icon: <UserPlus size={14} />, color: 'text-indigo-600', bg: 'bg-indigo-50' };

    return { icon: <Activity size={14} />, color: 'text-slate-500', bg: 'bg-slate-50' };
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden flex flex-col h-full sticky top-24">
      {/* --- Header --- */}
      <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
        <h3 className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-500 flex items-center gap-2">
          <Clock size={14} className="text-slate-400" /> System Audit Trail
        </h3>
        <span className="flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-blue-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
        </span>
      </div>

      {/* --- Logs List --- */}
      <div className="flex-1 overflow-y-auto p-4 space-y-5 custom-scrollbar">
        {logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 opacity-40">
            <Info size={32} className="mb-2" />
            <p className="text-center text-xs italic">No actions logged today.</p>
          </div>
        ) : (
          logs.map((log) => {
            const config = getActionConfig(log.action);
            return (
              <div key={log.id} className="relative flex gap-3 items-start group">
                {/* Visual Connector Line */}
                <div className="absolute left-[15px] top-8 bottom-[-20px] w-[1px] bg-gray-100 group-last:hidden" />
                
                {/* Action Icon */}
                <div className={`z-10 p-2 rounded-xl ${config.bg} ${config.color} shadow-sm group-hover:scale-110 transition-transform duration-200`}>
                  {config.icon}
                </div>

                <div className="flex-1 min-w-0 pt-0.5">
                  <p className="text-[11px] text-gray-900 leading-snug">
                    <span className="font-bold text-slate-800">{log.adminName}</span> 
                    <span className="text-gray-500 mx-1">processed</span>
                    <span className="font-black uppercase tracking-tighter text-[9px] px-1.5 py-0.5 bg-gray-100 rounded text-gray-600">
                        {log.action.replace('_', ' ')}
                    </span>
                  </p>
                  
                  <p className="text-[10px] font-bold text-blue-600 mt-1 truncate">
                    Target: {log.targetName}
                  </p>

                  {log.details && (
                    <p className="text-[9px] text-gray-400 mt-1 italic border-l-2 border-gray-100 pl-2">
                      "{log.details}"
                    </p>
                  )}

                  <div className="text-[8px] text-slate-300 font-bold uppercase mt-2 flex items-center gap-1">
                    <Clock size={8} />
                    {new Date(log.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* --- Footer --- */}
      <div className="p-3 bg-gray-50/50 border-t border-gray-100 text-center">
        <button className="text-[9px] font-black text-slate-400 uppercase tracking-widest hover:text-blue-600 transition-colors">
          Download Daily Report
        </button>
      </div>
    </div>
  );
};

export default RecentActivity;